import { useCallback, useEffect, useRef, useState } from 'react';
import { draftSaveIndicator, type DraftSaveQueue, type DraftSaveSnapshot } from '@dukanti/shared';
import type { EditorLeaveGuard, EditorLeaveState } from './use-editor-leave-guard';

export interface UseFailedSaveCloseGuardArgs {
  readonly queue: DraftSaveQueue;
  readonly snapshot: DraftSaveSnapshot;
  /** The normal close: send what is pending, then close. */
  readonly close: () => void;
  /** Close without sending anything (after "Leave without saving"). */
  readonly closeWithoutSaving: () => void;
}

export interface FailedSaveCloseGuard {
  /** Closes at once, or asks first when the last save failed. */
  readonly requestClose: () => void;
  /** The question's state and answers, in the shape `EditorLeaveDialog` reads. */
  readonly leaveGuard: EditorLeaveGuard;
}

/**
 * Epic #228 P6 (UI/UX P2-1): closing a region dialog after a FAILED save
 * asks, with the P4 leave answers (PD11): **Try again and leave** resends the
 * kept payload and closes once it is saved; **Leave without saving** drops
 * what the queue still holds; **Stay** keeps the dialog. Without a failure
 * the dialog closes as before (the queue flushes).
 */
export function useFailedSaveCloseGuard({
  queue,
  snapshot,
  close,
  closeWithoutSaving,
}: UseFailedSaveCloseGuardArgs): FailedSaveCloseGuard {
  const [state, setState] = useState<EditorLeaveState>('closed');
  const latest = useRef({ snapshot, close, closeWithoutSaving });
  latest.current = { snapshot, close, closeWithoutSaving };
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const requestClose = useCallback((): void => {
    if (draftSaveIndicator(latest.current.snapshot) === 'failed') setState('failed');
    else latest.current.close();
  }, []);

  const retry = useCallback((): void => {
    setState('saving');
    void queue.retry().then((result) => {
      if (!mounted.current) return;
      if (result.ok) {
        setState('closed');
        latest.current.close();
      } else {
        // Still failing: ask again. A conflict has its own banner in the dialog.
        setState(result.status === 'failed' ? 'failed' : 'closed');
      }
    });
  }, [queue]);

  const leave = useCallback((): void => {
    queue.discard();
    setState('closed');
    latest.current.closeWithoutSaving();
  }, [queue]);

  const stay = useCallback((): void => setState('closed'), []);

  return { requestClose, leaveGuard: { state, retry, leave, stay } };
}
